import React from 'react';
import { RangeInput } from './RangeInput';
import { CheckboxGroup } from '@/components/getStarted/checkbox/CheckboxGroup';
import { CheckboxOption } from '@/components/getStarted/checkbox/Checkbox';

import { RangeStepInput, RangeStepOption } from './RangeStepInput';
import {
  CardItemOption,
  CardItemsSelection,
} from '@/components/getStarted/CardItemSelection/CardItemsSelection';
import { FileUpload } from '@/components/getStarted/FileUpload';
import {
  ChangeStepFormEventValue,
  ChangeStepFormEvent,
} from '@/components/getStarted/multi-step-form/multi-step-form.model';
import layoutStyles from '@/components/getStarted/multi-step-form/layout.module.css';

interface InputFieldProps {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  value: ChangeStepFormEventValue;
  placeholder: string;
  options?: CheckboxOption[] | RangeStepOption[] | CardItemOption[];
  columns?: number;
  onChange: (e: ChangeStepFormEvent) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  textInputPlaceHolder?: string;
  textInputValue?: string;
  displayTextInput?: boolean;
  hasError?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  type = 'text',
  value,
  placeholder,
  options,
  columns,
  onChange,
  onBlur,
  textInputPlaceHolder,
  textInputValue,
  displayTextInput,
  hasError = false,
}) => {
  const errorRing = hasError ? 'ring-2 ring-red-400 rounded-md' : '';
  switch (type) {
    case 'card':
      return (
        <div className={`${layoutStyles.row} ${errorRing}`}>
          <label
            className="text-left font-semibold text-xl flex-1"
            htmlFor={name}
          >
            {label}
          </label>
          <CardItemsSelection
            items={options as CardItemOption[]}
            selectedItem={value as string}
            onChange={onChange}
            name={name}
            textInputPlaceHolder={textInputPlaceHolder}
            textInputValue={textInputValue}
            displayTextInput={displayTextInput}
          />
        </div>
      );

    case 'range':
      return (
        <div className={`${layoutStyles.row} ${errorRing}`}>
          <label
            className="text-left font-semibold text-xl flex-1"
            htmlFor={name}
          >
            {label}
          </label>
          <RangeInput
            value={value as number}
            min={1}
            max={20}
            onChange={onChange}
            name={name}
          />
        </div>
      );

    case 'stepRange':
      return (
        <div className={`${layoutStyles.row} ${errorRing}`}>
          <label
            className="text-left font-semibold text-xl flex-1"
            htmlFor={name}
          >
            {label}
          </label>
          <RangeStepInput
            value={value as string}
            onChange={onChange}
            name={name}
            options={options as RangeStepOption[]}
            key={name}
          />
        </div>
      );

    case 'checkbox':
      return (
        <div className={`${layoutStyles.row} ${errorRing}`}>
          <label
            className="text-left font-semibold text-xl flex-1"
            htmlFor={name}
          >
            {label}
          </label>
          <CheckboxGroup
            key={name}
            columns={columns}
            value={value as Array<string>}
            options={options as CheckboxOption[]}
            onChange={onChange}
            name={name}
            textInputPlaceHolder={textInputPlaceHolder}
            textInputValue={textInputValue}
            displayTextInput={displayTextInput}
          />
        </div>
      );

    case 'file':
      return (
        <div className={`${layoutStyles.row} ${errorRing}`}>
          <label
            className="text-left font-semibold text-xl flex-1"
            htmlFor={name}
          >
            {label}
          </label>
          <FileUpload name={name} onChange={onChange} value={value as File} />
        </div>
      );

    case 'textarea':
      return (
        <div className={layoutStyles.row}>
          <label
            className="text-left font-semibold text-xl flex-1"
            htmlFor={name}
          >
            {label}
          </label>
          <textarea
            id={name}
            name={name}
            value={value as string}
            placeholder={placeholder}
            onChange={onChange}
            className={`${layoutStyles.item} flex-1 p-4 rounded-md focus:outline-2 focus:outline-primary-green placeholder:text-[#707071] outline-[1.5px] ${hasError ? 'outline-red-400' : 'outline-[#cfcfd0]'} outline h-32`}
          />
        </div>
      );

    default:
      return (
        <div className={layoutStyles.row}>
          <label
            className="text-left font-semibold text-xl flex-1"
            htmlFor={name}
          >
            {label}
          </label>
          <input
            type={type}
            id={name}
            name={name}
            value={value as string}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            className={`${layoutStyles.item} flex-1 p-4 rounded-md focus:outline-2 focus:outline-primary-green placeholder:text-[#707071] outline-[1.5px] ${hasError ? 'outline-red-400' : 'outline-[#cfcfd0]'} outline`}
          />
        </div>
      );
  }
};

export default InputField;
